import { inject, Injectable } from "@angular/core";
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from "@angular/router";
import { onCLS, onINP, onLCP } from "web-vitals";

import { environment } from "@environments/environment";
import { configuration } from "properties/config";
import { SESSION_CONSTANTS } from "@core/constants/auth-constants";
import { VERSION_INFO } from "@core/constants/version";
import { stripQueryAndFragment } from "@core/logging/navigation-breadcrumbs.service";
import {
  RumSample,
  RumSampleType,
} from "@core/logging/performance-monitor.types";

/** Flush once this many samples are buffered (also flushed on page hide). */
const BATCH_MAX = 20;
/** Long tasks shorter than this aren't worth reporting (matches the spec). */
const LONG_TASK_MIN_MS = 50;

/**
 * Collects Real-User-Monitoring metrics in the browser and beacons them to the
 * backend RUM endpoint (`POST /event-logs-frontend/rum`), which re-emits them as
 * Cloud Monitoring metrics for the self-hosted Grafana dashboard. No Firebase.
 * See docs/logging/04-performance-monitoring.md.
 *
 * - Gated on `environment.LOG_CONFIG.remote` so local dev never reports.
 * - PII-safe: route templates only (no query string), numeric values only.
 * - Uses `fetch(keepalive)` + `credentials:"include"` so the auth cookie rides
 *   along and in-flight beacons survive page unload; bypasses HttpClient
 *   interceptors so reporting can't recurse through the app's request pipeline.
 *
 * Call `init()` once at bootstrap (app initializer in app.config.ts).
 */
@Injectable({ providedIn: "root" })
export class PerformanceMonitorService {
  private readonly router = inject(Router);

  private readonly buffer: RumSample[] = [];
  private navStartTs: number | null = null;
  private initialized = false;

  private get enabled(): boolean {
    return environment.LOG_CONFIG.remote === true;
  }

  /** Wire up the automatic collectors. Idempotent and a no-op when disabled. */
  init(): void {
    if (!this.enabled || this.initialized) {
      return;
    }
    this.initialized = true;

    this.registerWebVitals();
    this.registerRouteTiming();
    this.registerLongTaskObserver();
    this.registerFlushHandlers();
    this.recordInitialTti();
  }

  // ----- public record API (also called from the response interceptor) -----

  recordVital(name: Extract<RumSampleType, "LCP" | "INP" | "CLS">, value: number): void {
    this.enqueue({ type: name, value });
  }

  recordRouteTiming(route: string, durationMs: number): void {
    this.enqueue({ type: "route", value: durationMs, route });
  }

  recordLongTask(durationMs: number): void {
    this.enqueue({ type: "long_task", value: durationMs });
  }

  recordApiLatency(route: string, durationMs: number, status: number): void {
    this.enqueue({ type: "api_latency", value: durationMs, route, status });
  }

  recordTti(durationMs: number): void {
    this.enqueue({ type: "tti", value: durationMs });
  }

  // ----------------------------- collectors --------------------------------

  private registerWebVitals(): void {
    // web-vitals v4 replaced FID with INP (see spec). CLS is unitless; the
    // backend stores it on its own instrument.
    onLCP((m) => this.recordVital("LCP", m.value));
    onINP((m) => this.recordVital("INP", m.value));
    onCLS((m) => this.recordVital("CLS", m.value));
  }

  private registerRouteTiming(): void {
    this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationStart) {
        this.navStartTs = performance.now();
      } else if (event instanceof NavigationEnd) {
        if (this.navStartTs !== null) {
          const duration = performance.now() - this.navStartTs;
          this.recordRouteTiming(
            stripQueryAndFragment(event.urlAfterRedirects),
            duration
          );
          this.navStartTs = null;
        }
      } else if (
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.navStartTs = null;
      }
    });
  }

  private registerLongTaskObserver(): void {
    if (typeof PerformanceObserver === "undefined") {
      return;
    }
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > LONG_TASK_MIN_MS) {
            this.recordLongTask(entry.duration);
          }
        }
      });
      observer.observe({ entryTypes: ["longtask"] });
    } catch {
      // `longtask` is not universally supported — skip silently.
    }
  }

  private recordInitialTti(): void {
    // Lightweight proxy: time from navigation start to DOM-interactive. A fuller
    // TTI polyfill can replace this later (see spec); one sample per load.
    try {
      const [nav] = performance.getEntriesByType("navigation");
      if (nav && nav.domInteractive > 0) {
        this.recordTti(nav.domInteractive);
      }
    } catch {
      // Navigation Timing L2 unavailable — skip.
    }
  }

  private registerFlushHandlers(): void {
    // Flush remaining samples when the page is being hidden/unloaded — this is
    // when Web Vitals like CLS/LCP finalize.
    const flush = () => this.flush();
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flush();
      }
    });
    window.addEventListener("pagehide", flush);
  }

  // --------------------------- buffering / transport -----------------------

  private enqueue(sample: RumSample): void {
    if (!this.enabled) {
      return;
    }
    this.buffer.push(sample);
    if (this.buffer.length >= BATCH_MAX) {
      this.flush();
    }
  }

  private flush(): void {
    if (!this.enabled || this.buffer.length === 0) {
      return;
    }

    const samples = this.buffer.splice(0, this.buffer.length);
    const body = JSON.stringify({
      region: sessionStorage.getItem(SESSION_CONSTANTS.X_REGION),
      app_version: VERSION_INFO.appVersion,
      samples,
    });

    try {
      const xRegion = sessionStorage.getItem(SESSION_CONSTANTS.X_REGION);
      void fetch(environment.apiUrl + configuration.partialUrls.rum.ingest, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(xRegion ? { "X-Region": xRegion } : {}),
        },
        body,
        credentials: "include",
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // Never let telemetry transport surface as an app error.
    }
  }
}
